import app from 'flarum/forum/app';
import { extend } from 'flarum/common/extend';
import DiscussionListItem from 'flarum/forum/components/DiscussionListItem';
import Tooltip from 'flarum/common/components/Tooltip';
import Icon from 'flarum/common/components/Icon';
import highlight from 'flarum/common/helpers/highlight';
import extractText from 'flarum/common/utils/extractText';
import normaliseQuery from './utils/normaliseQuery';

/**
 * BM25 score of a typical strong keyword hit on a forum corpus; anything at
 * or above it fills the keyword meter.
 */
const STRONG_LEXICAL_SCORE = 20;

/**
 * Raw cosine range mapped onto the meaning meter: ~0.3 is unrelated text for
 * OpenAI's text-embedding-3 models, ~0.75 a close paraphrase.
 */
const UNRELATED_COSINE = 0.3;
const PARAPHRASE_COSINE = 0.75;
const BADGES = {
  lexical: {
    icon: 'fas fa-search',
    label: 'glowingblue-search.forum.badge.keyword',
    tooltip: 'glowingblue-search.forum.badge.keyword_tooltip'
  },
  semantic: {
    icon: 'fas fa-wand-magic-sparkles',
    label: 'glowingblue-search.forum.badge.ai',
    tooltip: 'glowingblue-search.forum.badge.ai_tooltip'
  },
  hybrid: {
    icon: 'fas fa-star',
    label: 'glowingblue-search.forum.badge.keyword_ai',
    tooltip: 'glowingblue-search.forum.badge.keyword_ai_tooltip'
  }
};

/**
 * Shows how each result was matched, and swaps core's excerpt for the
 * passage the searcher matched. Hooks DiscussionListItem, so it covers both
 * the SearchModal tabs (via MinimalDiscussionListItem) and the `/?q=` page.
 */
export default function decorateResults() {
  extend(DiscussionListItem.prototype, 'infoItems', function (items) {
    var _this$attrs$params;
    const q = (_this$attrs$params = this.attrs.params) == null ? void 0 : _this$attrs$params.q;
    if (!q) return;
    const post = this.attrs.post;
    const signals = post ? post.matchSignals() : this.attrs.discussion.matchSignals();
    if (!signals || normaliseQuery(post ? 'posts' : 'discussions', q) !== normaliseQuery(null, signals.query)) return;
    if (signals.type !== 'unknown') {
      items.add('glowingblueMatch', renderMatch(signals), 100);
    }
    if (signals.snippet) {
      const excerpt = highlight(signals.snippet, this.highlightRegExp, 175);
      if (items.has('excerpt')) items.setContent('excerpt', excerpt);else items.add('excerpt', excerpt, -100);
    }
  });
}
function renderMatch(signals) {
  var _signals$lexical, _signals$semantic;
  const badge = BADGES[signals.type];
  const lexical = Math.min(1, ((_signals$lexical = signals.lexical) != null ? _signals$lexical : 0) / STRONG_LEXICAL_SCORE);
  const semantic = clamp((((_signals$semantic = signals.semantic) != null ? _signals$semantic : UNRELATED_COSINE) - UNRELATED_COSINE) / (PARAPHRASE_COSINE - UNRELATED_COSINE));
  return m("span", {
    className: "GlowingBlueSearch-match"
  }, m(Tooltip, {
    text: extractText(app.translator.trans(badge.tooltip))
  }, m("span", {
    className: "GlowingBlueSearch-badge GlowingBlueSearch-badge--".concat(signals.type)
  }, m(Icon, {
    name: badge.icon
  }), app.translator.trans(badge.label))), m("span", {
    className: "GlowingBlueSearch-confidence",
    "aria-hidden": "true"
  }, meter('lexical', lexical), meter('semantic', semantic)));
}
function meter(kind, strength) {
  return m("span", {
    className: "GlowingBlueSearch-confidence-track"
  }, m("span", {
    className: "GlowingBlueSearch-confidence-fill GlowingBlueSearch-confidence-fill--".concat(kind),
    style: {
      width: "".concat(Math.round(strength * 100), "%")
    }
  }));
}
function clamp(value) {
  return Math.max(0, Math.min(1, value));
}